/**
 * Whether a category holds savings or expenditure.
 *
 * The single classifier. Nothing else in the app decides this — a second
 * implementation would let the numpad and the waterline disagree about which
 * categories exist (C-6).
 *
 * **Expenditure must be stated.** A blank, absent or unrecognised label means
 * savings; only a literal `"Expenditure"` means expenditure. That is the reverse
 * of the round-trip convention this replaced, where `null` and `"Expenditure"`
 * were interchangeable — which is exactly why the Dexie `version(6)` upgrade
 * stamps every pre-existing `null` as `"Expenditure"`. Without that backfill this
 * function would reclassify every category in every existing account
 * (`.claude/docs/CODING_STANDARDS.md`, scope expansion 2026-07-31).
 */

/**
 * The shape this reads. Structurally satisfied by `Category`, declared here so
 * `src/domain/` stays free of the Dexie row types (W-1).
 */
export interface CategoryKindRow {
  readonly savingsOrExpenditure?: string | null;
  readonly isFallback: boolean;
}

/**
 * The two canonical labels.
 *
 * They live here rather than with the budget sheet that used to own them,
 * because they stopped being sheet vocabulary the moment they drove math — and a
 * label the ceiling depends on cannot be defined in the CSV layer that merely
 * round-trips it.
 */
export const SAVINGS_LABEL = "Savings";
export const EXPENDITURE_LABEL = "Expenditure";

/** The one label that means expenditure. Anything else is savings. */
const EXPENDITURE_PATTERN = /^expenditure$/i;

/**
 * True when this category holds savings rather than spending.
 *
 * The fallback category is never savings, whatever its label says. It is what
 * keeps every expense categorised (`SPEC.md` §4), so a savings `Other` would
 * leave the numpad with nothing to log against — and unlike every other
 * category, there is no way for the user to route around it.
 */
export function isSavingsCategory(category: CategoryKindRow): boolean {
  if (category.isFallback) return false;
  return !EXPENDITURE_PATTERN.test((category.savingsOrExpenditure ?? "").trim());
}

/** The one label that means the user said "savings" in as many words. */
const SAVINGS_PATTERN = /^savings$/i;

/**
 * **Why** a category counts as savings — `null` when it does not.
 *
 * `isSavingsCategory` answers whether a category's budget comes off the salary.
 * This answers the question the user actually asks when that total is wrong:
 * *why is this in here?* The two cases it separates are not cosmetic.
 *
 * - `stated` — the user chose Savings. Nothing to explain.
 * - `labelled` — a free label their own sheet chose, "CPF" say. It classifies as
 *   savings because only a literal `"Expenditure"` does not.
 * - `unlabelled` — the label is blank or absent. This is the one that cost a
 *   real ledger: `budget_sheet.ts` writes `Savings` for an empty column 10, and
 *   a category can arrive here without anyone ever choosing anything.
 *
 * Derived from `isSavingsCategory` rather than re-testing the label, so a change
 * to what counts as expenditure cannot leave the explanation disagreeing with
 * the classification (C-6).
 */
export type SavingsReason =
  | { readonly kind: "stated" }
  | { readonly kind: "labelled"; readonly label: string }
  | { readonly kind: "unlabelled" };

export function savingsReason(category: CategoryKindRow): SavingsReason | null {
  if (!isSavingsCategory(category)) return null;

  const label = (category.savingsOrExpenditure ?? "").trim();
  if (label === "") return { kind: "unlabelled" };
  if (SAVINGS_PATTERN.test(label)) return { kind: "stated" };
  return { kind: "labelled", label };
}
